// Records the visualizer canvas plus the master audio into one video file.
// MP4 (H.264/AAC) when the browser can, which is what Instagram and TikTok
// want; WebM otherwise (Firefox).

const CANDIDATES = [
  'video/mp4;codecs=avc1.640028,mp4a.40.2',
  'video/mp4;codecs=avc1.42E01E,mp4a.40.2',
  'video/mp4;codecs=avc1,opus',
  'video/mp4',
  'video/webm;codecs=vp9,opus',
  'video/webm;codecs=vp8,opus',
  'video/webm',
];

export function pickVideoType(): string | null {
  if (typeof MediaRecorder === 'undefined') return null;
  return CANDIDATES.find((t) => MediaRecorder.isTypeSupported(t)) ?? null;
}

export const extensionFor = (type: string): string => (type.startsWith('video/mp4') ? 'mp4' : 'webm');

export interface VideoResult {
  blob: Blob;
  type: string;
  ext: string;
}

export class VideoCapture {
  private recorder: MediaRecorder | null = null;

  get supported(): boolean {
    return pickVideoType() !== null && typeof HTMLCanvasElement.prototype.captureStream === 'function';
  }

  start(canvas: HTMLCanvasElement, audio: MediaStream): boolean {
    const type = pickVideoType();
    if (!type) return false;
    const stream = new MediaStream([...canvas.captureStream(30).getVideoTracks(), ...audio.getAudioTracks()]);
    const rec = new MediaRecorder(stream, {
      mimeType: type,
      videoBitsPerSecond: 10_000_000,
      audioBitsPerSecond: 256_000,
    });
    // Chunks live with their recorder, so overlapping recordings can't mix.
    const chunks: Blob[] = [];
    rec.ondataavailable = (e) => {
      if (e.data.size) chunks.push(e.data);
    };
    rec.onstop = () => {
      rec.stream.getVideoTracks().forEach((t) => t.stop());
      const plain = type.split(';')[0];
      this.done.get(rec)?.({ blob: new Blob(chunks, { type: plain }), type: plain, ext: extensionFor(type) });
      this.done.delete(rec);
    };
    rec.start(250);
    this.recorder = rec;
    return true;
  }

  private done = new Map<MediaRecorder, (r: VideoResult) => void>();

  stop(): Promise<VideoResult | null> {
    const rec = this.recorder;
    this.recorder = null;
    if (!rec || rec.state === 'inactive') return Promise.resolve(null);
    return new Promise((resolve) => {
      this.done.set(rec, resolve);
      rec.stop();
    });
  }
}
